'use client';

import { useState } from 'react';

import type { Provider } from '@/app/models/providersResponse';
import type { ResourceDonorsResponse } from '@/app/models/resourceDonors';
import { useDeleteDonorMutation } from '@/app/services/resourceDonors';
import formatterPercentualValue from '@/app/utils/percentualUtils';
import TruncatedTextWithTooltip from '@/app/utils/truncatedTextWithTooltip';
import ActionMenu, { ItemAction } from '@/components/common/ActionMenu';
import DataTable, { bodyCellSx, Column } from '@/components/common/DataTable';
import StandardDialog from '@/components/common/StandardDialog';
import { TableCell, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface ResourceDonorsTableProps {
  rows: ResourceDonorsResponse[];
  setDrawerOpen: (open: boolean) => void;
  setSelectedItem: (p: Provider) => void;
  setSelectedAllocationPercent: (alocationPercent: string) => void;
  setSelectedEligiblePercent: (eligible: string) => void;
  setSelectedOriginItem: (toallocate: Provider) => void;
}

export default function ResourceDonorsTable(props: ResourceDonorsTableProps) {
  const theme = useTheme();
  const {
    rows,
    setDrawerOpen,
    setSelectedItem,
    setSelectedAllocationPercent,
    setSelectedOriginItem,
    setSelectedEligiblePercent,
  } = props;
  const { t } = useTranslation(['resourceDonor', 'common']);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);

  const [deleteDonor] = useDeleteDonorMutation();

  const handleDelete = (id: number) => {
    deleteDonor(id);
  };

  const columns: Column[] = [
    { key: 'donorProject', label: t('donor.project'), width: theme.spacing(50) },
    { key: 'allocationProject', label: t('allocation.project'), width: theme.spacing(80) },
    { key: 'branch', label: t('common:branch') },
    { key: 'eligible', label: t('eligible'), align: 'center' },
    { key: 'maxAllocation', label: t('maximum.allocation'), align: 'center' },
    { key: 'actions', label: t('common:actions'), align: 'right' },
  ];

  const renderRow = (row: ResourceDonorsResponse) => (
    <>
      <TableCell sx={bodyCellSx}>
        <TruncatedTextWithTooltip value={`${row.provider.code} - ${row.provider.name}`} sx={bodyCellSx} size={40} />
      </TableCell>
      <TableCell sx={bodyCellSx}>
        <TruncatedTextWithTooltip value={`${row.toAllocate.code} - ${row.toAllocate.name}`} sx={bodyCellSx} size={40} />
      </TableCell>
      <TableCell sx={bodyCellSx}>{row.provider.branchName}</TableCell>
      <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>{formatterPercentualValue(row.eligiblePercent)}</TableCell>
      <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>{formatterPercentualValue(row.maxAllocation)}</TableCell>
      <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
        <ActionMenu>
          <ItemAction
            text={t('common:edit') as string}
            actionType="edit"
            onClick={() => {
              setSelectedItem(row.toAllocate);
              setSelectedAllocationPercent(row.maxAllocation.toString());
              setSelectedOriginItem(row.provider);
              setSelectedEligiblePercent(row.eligiblePercent.toString());
              setDrawerOpen(true);
            }}
          />
          <ItemAction
            text={t('common:delete') as string}
            actionType="delete"
            onClick={() => {
              setSelectedRowId(row.id);
              setIsDeleteDialogOpen(true);
            }}
          />
        </ActionMenu>
      </TableCell>
    </>
  );

  // Use the row ID as the unique key
  const getRowKey = (row: ResourceDonorsResponse) => row.provider.id;

  return (
    <>
      <DataTable
        columns={columns}
        data={rows}
        renderRow={renderRow}
        getRowKey={getRowKey}
        emptyMessage={t('common:no.data.for.the.chosen.period.and.search.term')}
        stickyHeader
        size="small"
      />

      <StandardDialog
        title={t('common:confirm.delete.title')}
        contentText={t('common:confirm.delete.message')}
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={() => {
          if (selectedRowId) {
            handleDelete(selectedRowId);
          }
          setIsDeleteDialogOpen(false);
          setSelectedRowId(null);
        }}
        cancelText={t('common:no')}
        confirmText={t('common:yes')}
      />
    </>
  );
}
